import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useLogin } from '@vb-django/components/login/context/LoginContext';

import type { OpenCheckoutOptions } from '../../Checkout.types';

import SubscriptionCheckout from './SubscriptionCheckout';

export type { OpenCheckoutOptions };

export type SubscriptionCheckoutOpenContextValue = {
  openCheckout: (options?: OpenCheckoutOptions) => void;
  closeCheckout: () => void;
  /** Last step index (for deep links); mirrors logged-in step count in new flow. */
  stepCount: number;
};

const Context = createContext<SubscriptionCheckoutOpenContextValue | null>(null);

/**
 * Provides the new subscription checkout (Checkout.tsx + CheckoutContext) and helpers
 * to open/close it from anywhere under this tree.
 */
export const SubscriptionCheckoutWrapper = ({ children }: { children: React.ReactNode }) => {
  const { isLogged } = useLogin() ?? { isLogged: false };
  const [isOpen, setIsOpen] = useState(false);
  const [openOptions, setOpenOptions] = useState<OpenCheckoutOptions | null>(null);

  const stepCount = useMemo(() => (isLogged ? 3 : 4), [isLogged]);

  const openCheckout = useCallback((options?: OpenCheckoutOptions) => {
    setOpenOptions(options ?? null);
    setIsOpen(true);
  }, []);

  const closeCheckout = useCallback(() => {
    setIsOpen(false);
    setOpenOptions(null);
  }, []);

  const value = useMemo<SubscriptionCheckoutOpenContextValue>(
    () => ({
      openCheckout,
      closeCheckout,
      stepCount
    }),
    [openCheckout, closeCheckout, stepCount]
  );

  return (
    <Context.Provider value={value}>
      {children}
      <SubscriptionCheckout
        isOpen={isOpen}
        onClose={closeCheckout}
        openOptions={openOptions}
        onConsumedOpenOptions={() => setOpenOptions(null)}
      />
    </Context.Provider>
  );
};

/** @throws if used outside SubscriptionCheckoutWrapper */
export const useOpenSubscriptionCheckout = (): SubscriptionCheckoutOpenContextValue => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useOpenSubscriptionCheckout must be used within SubscriptionCheckoutWrapper');
  }
  return ctx;
};

/** Same as useOpenSubscriptionCheckout but returns null when no wrapper (e.g. marketing pages). */
export const useOpenSubscriptionCheckoutOptional =
  (): SubscriptionCheckoutOpenContextValue | null => useContext(Context);
